import React from 'react';
import { Autocomplete, TextField } from '@mui/material';
import MaterialSymbol from '../../common/MaterialSymbol';
import '../marketplace-design.css';

export interface TagInputProps {
    label: string;
    helperText?: string;
    value: string[];
    onChange: (tags: string[]) => void;
    /** Bereits verwendete Schlagwörter des Mandanten (Vorschläge) */
    suggestions?: string[];
    disabled?: boolean;
    /** Beschriftung des Entfernen-Buttons je Schlagwort */
    removeLabel?: (tag: string) => string;
}

const MAX_TAG_LENGTH = 200;

/** Normalisiert eine Eingabe-Liste: trimmen, leere und doppelte (ohne Groß-/Kleinschreibung) verwerfen. */
export const normalizeTags = (tags: string[]): string[] => {
    const seen = new Set<string>();
    const out: string[] = [];
    tags.forEach((raw) => {
        const tag = raw.trim().slice(0, MAX_TAG_LENGTH);
        const key = tag.toLowerCase();
        if (tag !== '' && !seen.has(key)) {
            seen.add(key);
            out.push(tag);
        }
    });
    return out;
};

/**
 * Chip-Eingabe für Schlagwörter: Enter oder Komma fügt hinzu, Backspace im
 * leeren Feld entfernt das letzte, Duplikate und Leerstrings werden ignoriert.
 * Beim Verlassen des Feldes wird ein angefangener Text übernommen. Gespeichert
 * wird weiterhin `string[]`.
 */
const TagInput: React.FC<TagInputProps> = ({ label, helperText, value, onChange, suggestions = [], disabled, removeLabel = (t) => t }) => {
    const [input, setInput] = React.useState('');

    const commit = (extra: string[] = []) => onChange(normalizeTags([...value, ...extra]));

    return (
        <Autocomplete
            multiple
            freeSolo
            autoSelect
            disableClearable
            disabled={disabled}
            className="mk-tagin"
            options={suggestions.filter((s) => !value.some((v) => v.toLowerCase() === s.toLowerCase()))}
            value={value}
            inputValue={input}
            onInputChange={(_, text, reason) => {
                if (reason === 'input' && text.includes(',')) {
                    // Komma schließt ein Schlagwort ab; mehrere eingefügte werden alle übernommen.
                    const parts = text.split(',');
                    const rest = parts.pop() ?? '';
                    commit(parts);
                    setInput(rest);
                    return;
                }
                setInput(text);
            }}
            onChange={(_, next) => {
                onChange(normalizeTags(next as string[]));
            }}
            renderTags={(tags, getTagProps) =>
                tags.map((tag, index) => {
                    const { key, onDelete } = getTagProps({ index });
                    return (
                        <span key={key} className="mk-pill neu" style={{ margin: '2px 4px 2px 0', paddingRight: 4 }} data-testid="tag-pill">
                            {tag}
                            {!disabled && (
                                <button
                                    type="button"
                                    onClick={onDelete}
                                    aria-label={removeLabel(tag)}
                                    style={{ border: 0, background: 'none', padding: 0, cursor: 'pointer', color: 'inherit', lineHeight: 1 }}
                                >
                                    <MaterialSymbol name="close" size={14} />
                                </button>
                            )}
                        </span>
                    );
                })
            }
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={label}
                    helperText={helperText}
                    size="small"
                    inputProps={{ ...params.inputProps, 'data-testid': 'tag-input' }}
                />
            )}
        />
    );
};

export default TagInput;
